import { useEffect, useState, Fragment } from 'react';
import { Book, Bug, CheckCircle, ExternalLink, Scale, X } from 'lucide-react';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';

import IconLabelFactory from './ui/IconLabelFactory';
import { cn } from '../lib/cn';
import { metaApi } from '../services/annotatorApi';
import type { VersionInfo } from '../types/api';

/**
 * AboutModal — mirrors agent-builder's AboutModal layout
 * (frontend/src/components/modals/AboutModal.jsx):
 *
 *   • Gradient header (primary-50→primary-50 light / primary-900/50→primary-900/50
 *     dark) with the brand square + title + subtitle + dismiss X
 *   • Centered version pill (bg-primary-500/15 dark, bg-primary-50 light)
 *   • Soft "description" block
 *   • "Key Features" emerald-tinted section with CheckCircle bullets
 *   • Links row (docs / report-issue / license)
 *   • Copyright divider
 *   • Gradient close button in the footer (from-primary-500 to-primary-600)
 *
 * This is NOT routed through the shared Modal component because the header
 * has a custom gradient + brand-mark layout that doesn't fit the standard
 * form-modal pattern.
 */
interface AboutModalProps {
    open: boolean;
    onClose: () => void;
    onOpenLicense?: () => void;
    isDarkMode?: boolean;
}

const FRONTEND_VERSION = import.meta.env.VITE_VERSION ?? 'dev';
const BUILD_DATE = import.meta.env.VITE_BUILD_DATE ?? '';
const COMMIT_SHA = import.meta.env.VITE_COMMIT_SHA ?? '';
const DOCS_URL = import.meta.env.VITE_DOCS_URL ?? '';

export default function AboutModal({ open, onClose, onOpenLicense, isDarkMode = false }: AboutModalProps) {
    const { t } = useTranslation('about');
    const { t: tCommon } = useTranslation('common');
    const [backend, setBackend] = useState<VersionInfo | null>(null);

    useEffect(() => {
        if (!open) return;
        metaApi.version().then(setBackend).catch(() => setBackend(null));
    }, [open]);

    const dk = isDarkMode;
    const cardBg = dk ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200';
    const headerBg = dk
        ? 'bg-gradient-to-r from-primary-900/50 to-primary-900/50 border-gray-700'
        : 'bg-gradient-to-r from-primary-50 to-primary-50 border-gray-200';
    const footerBg = dk ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200';
    const textMain = dk ? 'text-gray-200' : 'text-gray-700';
    const textMuted = dk ? 'text-gray-400' : 'text-gray-500';
    const textTitle = dk ? 'text-gray-100' : 'text-gray-900';
    const closeBtn = dk
        ? 'hover:bg-gray-700 text-gray-400'
        : 'hover:bg-gray-200 text-gray-500';

    // Pulled from i18n "about" namespace — translator-friendly.
    const features: string[] = (t('features', { returnObjects: true, defaultValue: [] }) as string[]) ?? [];

    return (
        <Transition show={open} as={Fragment}>
            <Dialog onClose={onClose} className="relative z-50">
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
                    leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
                >
                    <div aria-hidden="true" className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
                        leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
                    >
                        <Dialog.Panel className={cn('w-full max-w-lg rounded-xl shadow-2xl border overflow-hidden flex flex-col max-h-[calc(100vh-40px)]', cardBg)}>
                            {/* Header */}
                            <div className={cn('px-6 py-4 border-b flex items-center justify-between flex-shrink-0', headerBg)}>
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-600 rounded-xl flex items-center justify-center text-white shadow-lg shadow-primary-500/30 ring-2 ring-primary-500/20">
                                        <IconLabelFactory className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0">
                                        <Dialog.Title className={cn('text-lg font-bold truncate', textTitle)}>
                                            {t('title')}
                                        </Dialog.Title>
                                        <p className={cn('text-xs truncate', textMuted)}>{t('product_name')}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className={cn('p-2 rounded-lg transition-colors flex-shrink-0', closeBtn)}
                                    aria-label={tCommon('close')}
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {/* Body */}
                            <div className={cn('px-6 py-5 flex-1 min-h-0 overflow-y-auto', textMain)}>
                                <div className="space-y-5">
                                    {/* Version pill */}
                                    <div className="flex items-center justify-center">
                                        <div className={cn(
                                            'inline-flex items-center gap-2 px-4 py-2 rounded-full',
                                            dk ? 'bg-primary-500/15 border border-primary-500/30' : 'bg-primary-50 border border-primary-200',
                                        )}>
                                            <span className={cn('text-xs font-medium', dk ? 'text-primary-400' : 'text-primary-600')}>
                                                {t('version_label_short', { defaultValue: 'Version' })}
                                            </span>
                                            <span className={cn('text-sm font-bold', dk ? 'text-primary-300' : 'text-primary-700')}>
                                                {FRONTEND_VERSION}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Description */}
                                    <div className={cn('p-4 rounded-lg', dk ? 'bg-gray-800/50' : 'bg-gray-50')}>
                                        <p className={cn('text-sm leading-relaxed text-center', dk ? 'text-gray-400' : 'text-gray-600')}>
                                            {t('description', { defaultValue: 'Annotate images with bounding boxes, train detection models, and deploy them — all without leaving IGNODE.' })}
                                        </p>
                                    </div>

                                    {/* Build details */}
                                    <div className={cn('text-xs text-center space-y-0.5', textMuted)}>
                                        <p>{t('build_label', { commit: COMMIT_SHA, date: BUILD_DATE })}</p>
                                        <p>{t('backend_label', { version: backend?.version ?? '—' })} · {t('runtime_label', { runtime: backend?.runtime ?? '—' })}</p>
                                    </div>

                                    {/* Features */}
                                    {features.length > 0 && (
                                        <div className={cn(
                                            'p-4 rounded-lg border',
                                            dk ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200',
                                        )}>
                                            <h3 className={cn(
                                                'text-sm font-bold mb-3 flex items-center gap-2',
                                                dk ? 'text-emerald-400' : 'text-emerald-700',
                                            )}>
                                                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                                                {t('key_features', { defaultValue: 'Key Features' })}
                                            </h3>
                                            <ul className="space-y-2">
                                                {features.map((f, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <CheckCircle size={14} className={cn('mt-0.5 flex-shrink-0', dk ? 'text-emerald-400' : 'text-emerald-600')} />
                                                        <span className={cn('text-sm', dk ? 'text-gray-300' : 'text-gray-600')}>{f}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Links */}
                                    <div className="flex items-center justify-center gap-6 pt-2 flex-wrap">
                                        {DOCS_URL && (
                                            <a href={DOCS_URL} target="_blank" rel="noreferrer"
                                                className={cn('flex items-center gap-2 text-sm transition-colors', dk ? 'text-gray-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-600')}>
                                                <Book size={14} /> {t('documentation')}
                                            </a>
                                        )}
                                        {onOpenLicense && (
                                            <button type="button" onClick={onOpenLicense}
                                                className={cn('flex items-center gap-2 text-sm transition-colors', dk ? 'text-gray-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-600')}>
                                                <Scale size={14} /> {t('license_and_agreement')}
                                            </button>
                                        )}
                                        <a href="https://ignode.com/support" target="_blank" rel="noreferrer"
                                            className={cn('flex items-center gap-2 text-sm transition-colors', dk ? 'text-gray-400 hover:text-primary-400' : 'text-gray-500 hover:text-primary-600')}>
                                            <Bug size={14} /> {t('report_issue')}
                                        </a>
                                    </div>

                                    {/* Copyright */}
                                    <div className={cn('text-center pt-3 border-t', dk ? 'border-gray-700' : 'border-gray-200')}>
                                        <p className={cn('text-xs', textMuted)}>
                                            {t('copyright', { defaultValue: 'Copyright © 2026 IGNODE. All rights reserved.' })}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Footer — gradient close button */}
                            <div className={cn('px-6 py-4 border-t flex justify-end flex-shrink-0', footerBg)}>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:from-primary-600 hover:to-primary-700 shadow-md inline-flex items-center gap-2"
                                >
                                    <ExternalLink size={14} className="opacity-0 w-0" aria-hidden="true" />
                                    {tCommon('close')}
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition>
    );
}
